import FlashableMessages from "./FlashableMessages.mixin";

/*
 * T33a (walk 2026-09-18, T33, Log #370 and #374). THE FOUR CENTRAL MESSAGE PIECES.
 *
 * ONE TITLE GRAMMAR AND ONE DELAY, decided here and never at the 277 call sites:
 * `Saved` / `Working` / `Not saved` at 4000ms. Only a toast's TITLE and its delay are the
 * design's; every message's own words are the caller's and are untouched.
 *
 * THE `.ftd` ANCESTOR. The merged sheet has 219 rules written `.ftd .ftd-something`, and both
 * `$bvToast` and `$bvModal.msgBoxConfirm` render at `<body>`, outside every `.ftd` element on the
 * page. So the class is put ON what they render: `modalClass` for a confirm, and the toaster's own
 * name (`toaster: 'ftd-toasts'`) for the stack, which is the only way to class a toaster in
 * BootstrapVue 2.23.1 -- there is no `toasterClass` option. A TOAST IS THE EXCEPTION: `$bvToast`
 * puts its own `.toast-body` between the `.toast` element and the content, and the sheet draws the
 * toast's mark with direct-child selectors, so the house box is rendered as content of our own
 * (see `presentToast`) and the `.toast` element is left a bare shell.
 * `resources/sass/custom/_ftd-messages.scss` carries the remainder and no look of its own.
 */

/*
 * T33a, second build. THE COMPOSITION MUST BE SOUND WITH ITS BINDING ABSENT.
 *
 * About forty node harnesses under `tests/Feature` (the `js` folders of Pass2, Ledger, Asset,
 * Pass1B and Department)
 * read THIS file as text, delete the line that brings `FlashableMessages` in, evaluate what is
 * left and hand the result to `Vue.mixin()`. A bare `mixins: [FlashableMessages]` then evaluates
 * to `mixins: [undefined]` and `Vue.mixin()` throws in `mergeOptions` ("Cannot read properties of
 * undefined (reading 'components')"), which is what took the first build back off the shared
 * branch (41f048b3). Two kinds of loader exist and they fail differently: most evaluate inside
 * `with (proxy)`, where the missing name reads as `undefined`; the Ledger ones evaluate inside a
 * plain `Function`, where reading it THROWS a ReferenceError. The `try` below answers both.
 *
 *  - In the real build webpack resolves the binding, `ftdComposedMixins` is `[FlashableMessages]`,
 *    `window.flash` is defined because that module is evaluated with this one, and every
 *    component carries `checkForFlashableMessages` -- the flash ships on every page.
 *  - With the line stripped, `ftdComposedMixins` is `[]`: a sound Vue mixin with no flash in it,
 *    which is exactly what those harnesses loaded before T33a. The two hooks below therefore ask
 *    for the method before they call it.
 *
 * The flash stays in `FlashableMessages.mixin.js` and is NOT copied into this file on purpose:
 * that file is where the brief puts the maps and the method, a second copy is a second thing to
 * keep in step, and a copy here would read `localStorage` from every component of every harness,
 * where there is none. Pinned by `tests/Feature/Module/GlobalMixinStrippedImportsTest.php`.
 */
const ftdComposedMixins = [];
try {
    if (FlashableMessages && typeof FlashableMessages === 'object') {
        ftdComposedMixins.push(FlashableMessages);
    }
} catch (e) {
    // the binding is absent (a harness stripped it): compose nothing
}

// the ids `$bvToast.hide(id)` and `$bvModal` need, one series per page load
let ftdMessageSeq = 0;

// THE ONE DELAY (Log #370, brief §6.2). Every toast the site raises stays on screen this long,
// whatever raised it -- a save, a failed save, a delete, a bulk action or a flash -- because the
// delay is decided here and not at the 277 call sites. See `presentToast`.
const FTD_TOAST_DELAY = 4000;

// THE ONE STACK ORDER (Amendment 8 / Log #398, brief §6.2's reason applied a third time). The newest
// toast is the one at the TOP of the stack, whatever raised it and whatever it passed, because the
// order is decided here and not at the 283 call sites -- 277 of which pass `appendToast` as `true`.
// See `presentToast`, where this is negated into BootstrapVue's own `appendToast`.
const FTD_TOAST_NEWEST_FIRST = true;

// ONE LIVE TOAST OF ONE KIND (DEFECTS F45, the walk's reading P2.6; the same reason a third time: the
// decision is here and not at the 283 call sites). Switching the assets list's View raises one
// "Saved View {X} Successfully" a switch, and switching quickly left them piled on top of each other at the
// top right. `$bvToast.hide(id)` needs the id of the toast to take off the screen, and `presentToast` is the
// only place that knows both the id it has just made and the kind of message it is about to raise, so the
// one LIVING id of each kind is kept here -- one map a page load, beside the sequence above.
// A stale entry is harmless and is left: hiding a toast that has already gone is a no-op in BootstrapVue,
// and the map holds one entry per SHAPE OF MESSAGE, never one per toast raised.
// See `ftdToastKind` for what a kind is and `ftdKeepOneToastOfKind` for the replacing.
const ftdLiveToasts = {};

// HOW MANY ANIMATION FRAMES A REPLACEMENT WAITS FOR THE TOAST IT REPLACES TO COME UP (F45).
// BootstrapVue only hides a toast that is ALREADY SHOWING -- `hide()` returns at once while `localShow` is
// false -- and `localShow` becomes true TWO animation frames after `$bvToast.toast()` is called
// (`bootstrap-vue/src/components/toast/toast.js`: `mounted()` -> `$nextTick` -> `requestAF` -> `show()` ->
// `$nextTick` -> `requestAF`). A toast raised from a server's answer is always older than that and the first
// `hide` is the one that works; this covers the BURST -- two raises inside the same frame -- which is what a
// fast hand on the View dropdown produces and is the case the walk recorded (P2.6). Measured without it: a
// three-switch burst left two toasts on screen, not one. Hiding a toast that has already gone is a no-op, so
// every frame of this ladder after the one that takes costs nothing.
const FTD_TOAST_REPLACE_FRAMES = 4;

// the four theme variables the sheet reads, as `FilmTvDashboard.vue` reads them. `.ftd` declares
// its own fall-backs, so a `.ftd` element only takes the entity's colours if they are set on it
// inline -- and neither `$bvToast` nor `$bvModal` lets a style reach the element it renders.
const ftdThemeVariables = () => {
    const plate = (typeof window !== 'undefined' && window.COLOUR_PLATE) ? window.COLOUR_PLATE : null;
    if (!plate) {
        return null;
    }
    const variables = {
        '--ftd-primary': plate.primary_colour,
        '--ftd-accent': plate.accent_colour,
        '--ftd-primary-soft': plate.soft_primary_colour,
        '--ftd-accent-soft': plate.soft_accent_colour,
    };
    const set = {};
    Object.keys(variables).forEach(function (name) {
        if (variables[name]) {
            set[name] = variables[name];
        }
    });
    return Object.keys(set).length ? set : null;
};

export default {
    mixins: ftdComposedMixins,
    data(){
        return {
            delete_checkbox: false,
            currentUserPermissions: null,
        }
    },
    mounted() {
        window.VueInstance = this;

        // T33a. The last `mounted()` of a page is the root's, so by here every component that
        // could claim an `added_disposition` event has been created. Anything still waiting is
        // delivered now, so a flash never outlives the page that was meant to show it.
        if (this === this.$root && typeof this.checkForFlashableMessages === 'function'){
            this.checkForFlashableMessages(true);
        }
    },
    created: function () {
        let currentUserPermissions = this.$root.shared_info ? this.$root.shared_info.currentUserPermissions : [];
        this.currentUserPermissions = [];
        if (Array.isArray(currentUserPermissions)){
            this.currentUserPermissions = currentUserPermissions;
        }else if (currentUserPermissions instanceof Object){
            this.currentUserPermissions = Object.values(currentUserPermissions);
        }

        // T33a. THE FLASH'S DELIVERY POINT. `Vue.mixin(Globals)` at `app.js:103` gives this
        // `created()` to every component, so every page delivers a flash whether or not the top
        // bar is mounted -- which is what the two module-switch blades need, since neither mounts
        // it. It is safe to run on all of them: `checkForFlashableMessages` reads and removes one
        // `localStorage` key, so the first component to see it wins and the rest are no-ops.
        // The root is skipped so that the page's own component, not the shell, is the first to
        // look: an `added_disposition` flash belongs to the host that has `afterSavingDisposition`.
        // The method is asked for first: it is absent where this file was loaded with its
        // composition stripped (see `ftdComposedMixins` at the head of the file).
        if (this !== this.$root && typeof this.checkForFlashableMessages === 'function'){
            this.checkForFlashableMessages();
        }
    },
    methods: {
        cancelClicked(){
            let modal_id = this.modal_id ? this.modal_id : (this.received_modal_id ? this.received_modal_id : 'create-'+this.item_type);
            this.$bvModal.hide(modal_id);
            this.$emit('on-modal-cancel');
        },
        /**
         * T33a. Put the entity's four theme variables on the `.modal` element `$bvModal` rendered
         * for us. `msgBoxConfirm` mounts its modal within the caller's own tick, so the element is
         * there to be found; `$bvToast` does NOT (it renders a tick or more later), which is why
         * the toast carries the variables on markup of its own instead and no longer calls this
         * (fix round 1, defect 3). `$bvModal` takes no style, and `.ftd` re-declares the four
         * variables, so an inherited value never reaches a `.ftd` element. Where there is no plate
         * the sheet's own fall-backs stand, which is what `.ftd` is written for.
         */
        ftdThemeOn(element_id){
            const variables = ftdThemeVariables();
            if (!variables){
                return;
            }
            this.$nextTick(() => {
                const el = document.getElementById(element_id);
                if (!el){
                    return;
                }
                Object.keys(variables).forEach(function (name) {
                    el.style.setProperty(name, variables[name]);
                });
            });
        },

        /**
         * T33a. THE ONE TITLE GRAMMAR AND THE ONE LOOK, by the variant the caller already passes.
         * `Saved` / `Working` / `Not saved` (Log #370), and the sheet's three toast variants.
         *
         * HOTFIX 2026-10-12, entry 9 (R4 yes, Amendment 13:50Z): a `warning` is a partial outcome, not a
         * refusal, so it has its own fourth word and the amber look: "Check", `bx-error`, `ftd-toast--warn`
         * (the sheet's two `.ftd-toast--warn` rules). SHARED: every module's warning toast changes with it.
         */
        ftdToastLook(variant){
            if (variant === 'success'){
                return { title: 'Saved', icon: 'bx-check-circle', variant_class: 'ftd-toast--good' };
            }
            if (variant === 'warning'){ return { title: 'Check', icon: 'bx-error', variant_class: 'ftd-toast--warn' }; }
            if (variant === 'danger' || variant === 'error'){
                return { title: 'Not saved', icon: 'bx-error-circle', variant_class: 'ftd-toast--bad' };
            }
            return { title: 'Working', icon: 'bx-info-circle', variant_class: null };
        },

        /**
         * F45 (the walk's reading P2.6). THE KIND OF A TOAST: the SHAPE of what it says, in the variant it
         * says it in. Two toasts of one kind are the same sentence about different things, and the newer
         * replaces the older (see `ftdKeepOneToastOfKind`).
         *
         * WHY NOT THE VARIANT ALONE, which would have been one line less. The messages that piled up are
         * not equal: `saveView` builds "Saved View {Storage} Successfully", then "Saved View {Labels}
         * Successfully" (`PageContent.vue`), so a rule that replaced only an IDENTICAL message would have
         * left the pile exactly as the walk found it. And a rule that replaced every toast of a variant
         * would have taken a refused save off the screen to show another refused save, which is a message
         * the user has not read yet.
         *
         * SO THE MESSAGE IS REDUCED TO ITS SHAPE, and the site's own message grammar is what makes that
         * honest: the thing a message is about is written inside braces or parentheses ("Saved View {X}",
         * "Saved Layout {X}", "Saved Sorting {X}") or as a number ("Updated Asset #2599619 Successfully"),
         * and the words round it are the sentence. So what stands inside braces, brackets or parentheses
         * becomes `{}`, a run of digits becomes `#`, spacing and case are flattened, and the variant class
         * is carried with the result. A save and a refusal are two kinds; a view and a layout are two
         * kinds; two saved views are one.
         *
         * A MESSAGE THAT IS NOT TEXT HAS NO KIND. `presentToast`'s `message` may be VNodes (it is passed
         * through untouched, string or VNodes), and nothing can be read off those without rendering them:
         * the answer is `null`, and a toast with no kind replaces nothing and is replaced by nothing.
         */
        ftdToastKind(message, variant_class){
            if (typeof message !== 'string' && typeof message !== 'number'){
                return null;
            }

            const shape = String(message)
                .replace(/[{(\[][^{}()\[\]]*[})\]]/g, '{}')
                .replace(/\d+/g, '#')
                .replace(/\s+/g, ' ')
                .trim()
                .toLowerCase();

            return (variant_class || 'ftd-toast') + '|' + shape;
        },

        /**
         * F45. ONE LIVE TOAST OF ONE KIND: the newer REPLACES the older, which is the first of the two
         * answers the row allows ("Queue or replace toasts of one kind"). Replacing is the one that never
         * makes the reader wait: a queued toast would show the view the user has already left, for four
         * seconds, after they had chosen another.
         *
         * The previous toast of this kind is hidden by its id, so it fades out the way `$bvToast` fades
         * every toast out and the stack closes up on its own. Hiding an id that has already gone (it timed
         * out, or its close was pressed) is a no-op, which is why nothing has to be cleaned up when a toast
         * ends by itself.
         */
        ftdKeepOneToastOfKind(kind, toast_id){
            if (!kind){
                return;
            }

            const living = ftdLiveToasts[kind];
            ftdLiveToasts[kind] = toast_id;
            if (living && living !== toast_id){
                this.ftdHideToastWhenItIsUp(living, FTD_TOAST_REPLACE_FRAMES);
            }
        },

        /**
         * F45. HIDE A TOAST EVEN IF IT HAS NOT FINISHED COMING UP. BootstrapVue's `hide` is heard by a
         * toast that is already showing and by no other (`toast.js`, `hide()` returns while `localShow` is
         * false), and a toast needs two animation frames from `$bvToast.toast()` to get there -- so a burst
         * of raises inside one frame, which is exactly what the walk saw on the View dropdown, would leave
         * the middle ones on screen for ever. The ask is repeated for a few frames, and every repeat after
         * the one that takes is a no-op. See `FTD_TOAST_REPLACE_FRAMES`.
         */
        ftdHideToastWhenItIsUp(toast_id, frames_left){
            this.$root.$bvToast.hide(toast_id);
            if (frames_left > 0 && typeof requestAnimationFrame === 'function'){
                requestAnimationFrame(() => {
                    this.ftdHideToastWhenItIsUp(toast_id, frames_left - 1);
                });
            }
        },

        /**
         * THE TOAST. The signature does not move: positional, title first, six parameters, the
         * same order, because 277 call sites in 174 files pass it positionally. The one default
         * that moved is the delay's, 5000 -> 4000, which is the design's own decision.
         *
         * THREE OF THE SIX ARGUMENTS ARE NO LONGER THE CALLER'S, and for one reason: a decision of
         * the design belongs in the helper and never at the call sites (brief §6.2; and for the
         * third, the hand-off's Amendment 8 / Log #398, T33a's follow-up 1).
         *
         *  - `title` is still accepted and is still the first parameter, but the word drawn is the
         *    grammar's, not the caller's: "Success", "Success!", "Info" and "Error" were four
         *    titles for one thing.
         *  - `autoHideDelay` is accepted and IGNORED in exactly the same way (fix round 3,
         *    defect 1). Moving only the default would have left the decision at the call sites:
         *    271 of the 277 pass the fourth argument explicitly -- 242 pass 3000, 12 pass 1500,
         *    7 pass 6000, 5 pass 5000 -- so all but about eleven toasts on the site would still
         *    have run at their own delay and Log #370's one delay would be in force nowhere.
         *    `FTD_TOAST_DELAY` is that one delay, and the 4000 in the signature is kept so the
         *    six parameters and their defaults read as the brief writes them.
         *
         *  - `appendToast` is accepted and IGNORED FOR THE STACK'S ORDER (follow-up 1, the
         *    hand-off's Amendment 8, Log #398). THE NEWEST TOAST GOES ON TOP: task T33 Wanted 1's
         *    first bullet, walk step 3 and the sheet's own comment at `film-tv-dashboard.css:1001`
         *    all say so, and BootstrapVue draws the stack the other way round when `appendToast`
         *    is true (`bootstrap-vue/src/components/toast/toast.js:218` sets the portal's
         *    `order` to `Date.now() * (appendToast ? 1 : -1)`, and portal-vue sorts its transports
         *    by `a.order - b.order`, so a positive order puts the newest LAST). Moving only the
         *    default would have decided nothing: 277 of the 283 call sites pass the sixth argument
         *    explicitly as `true`, so all but six toasts on the site would still stack newest at
         *    the bottom. `FTD_TOAST_NEWEST_FIRST` is that one decision. The parameter stays in the
         *    signature, in its place and unused for ordering, so NO CALL SITE IS TOUCHED.
         *
         * `message` is the caller's and is passed through untouched, string or VNodes. `solid` is
         * still the caller's and still reaches `$bvToast`.
         *
         * AND A FOURTH DECISION IS TAKEN HERE, F45's (the walk's reading P2.6): A NEW TOAST OF A KIND
         * ALREADY ON SCREEN REPLACES IT rather than piling on top of it. No call site is touched for that
         * either -- the kind is read off the message the caller already passes. See `ftdToastKind` and
         * `ftdKeepOneToastOfKind`.
         */
        presentToast(title, message, variant = "success", autoHideDelay = 4000, solid=true, appendToast=true) {
            const h = this.$createElement;
            const look = this.ftdToastLook(variant);
            const toast_id = 'ftd-toast-' + (++ftdMessageSeq);
            const hide = () => { this.$root.$bvToast.hide(toast_id); };

            // r12 screens 3 and 4: an icon, `.ftd-toast__text` holding `.ftd-toast__title` over
            // `.ftd-toast__line`, then `.ftd-toast__x`
            // (`r12-messages-and-page-states.html:189-193`, `:255-259`).
            //
            // THE BOX IS THE CONTENT, NOT THE `.toast` ELEMENT (fix round 1, defects 2 and 3).
            // `$bvToast` always wraps what it is given in its own `.toast-body`, so a `.ftd-toast`
            // put on the `.toast` element through `toastClass` has that wrapper between it and the
            // icon -- and the sheet draws the mark with DIRECT-CHILD selectors (`.ftd-toast > i`
            // at `:1004`, `.ftd-toast--good > i` at `:1011`, `.ftd-toast--bad > i` at `:1013`).
            // `display: contents` takes the wrapper out of the LAYOUT but not out of SELECTOR
            // MATCHING, so the tick and the cross came out grey and 13px instead of the sheet's
            // colour at 1.2rem.
            //
            // So the house box is rendered HERE, as the mock renders it: one element carrying
            // `ftd ftd-toast ftd-toast--good|--bad`, with the icon, the text and the close as its
            // own direct children, and the entity's four theme variables inline on it exactly as
            // every r12 screen writes them (`r12-messages-and-page-states.html:158`). Rendering
            // them here also cures the third defect: `ftdThemeOn` looked the element up in the
            // CALLER's `$nextTick`, but `$bvToast` renders the toast a tick or more later, so it
            // found nothing and the plate never reached a toast. There is nothing to look up now.
            // The `.toast` element `$bvToast` renders is left as a bare shell, and
            // `_ftd-messages.scss` turns Bootstrap's own box off on it.
            //
            const box = h('div', {
                class: ['ftd', 'ftd-toast', look.variant_class].filter(Boolean),
                style: ftdThemeVariables() || {}
            }, [
                // r12 screens 3 and 4: an icon, `.ftd-toast__text` holding `.ftd-toast__title`
                // over `.ftd-toast__line`, then `.ftd-toast__x`
                // (`r12-messages-and-page-states.html:189-193`, `:255-259`).
                h('i', { class: ['bx', look.icon] }),
                h('span', { class: 'ftd-toast__text' }, [
                    h('b', { class: 'ftd-toast__title' }, look.title),
                    h('span', { class: 'ftd-toast__line' }, [message])
                ]),
                h('button', {
                    class: 'ftd-toast__x',
                    attrs: { type: 'button', title: 'Close' },
                    on: { click: hide }
                }, [
                    h('i', { class: 'bx bx-x', attrs: { 'aria-hidden': 'true' } }),
                    h('span', { class: 'ftd-sr' }, 'Close')
                ])
            ]);

            // F45: ONE LIVE TOAST OF ONE KIND. A toast that says the same sentence about another thing --
            // the saved view switched twice, the same field saved twice -- takes the place of the one on
            // screen instead of piling on top of it. What counts as one kind, and why it is not the variant
            // alone, is in `ftdToastKind`; a message given as VNodes has no kind and replaces nothing.
            this.ftdKeepOneToastOfKind(this.ftdToastKind(message, look.variant_class), toast_id);

            this.$root.$bvToast.toast([box], {
                id: toast_id,
                // the stack: `.ftd-toasts` is fixed at the top right, one width, newest at the top
                toaster: 'ftd-toasts',
                // the house markup carries its own cross, and `variant` is NOT passed on: a
                // `b-toast-{variant}` would paint the box green or red at `.b-toast-x .toast`
                // (0,0,2), where the design is a white box with a coloured left edge and icon.
                noCloseButton: true,
                // the one delay, decided here: see the note on `autoHideDelay` above
                autoHideDelay: FTD_TOAST_DELAY,
                solid: solid,
                // THE NEWEST TOAST GOES ON TOP, whatever the caller passed: see the note on
                // `appendToast` above. BootstrapVue's `appendToast: false` is its "prepend", and
                // prepend is newest-first, so the one decision is negated exactly here.
                appendToast: !FTD_TOAST_NEWEST_FIRST
            });
        },


        /**
         * T33a. SHAPE THREE'S OWN DEFAULTS, in one place, so an adopter needs no copy of them.
         * `type` is `true`, or an object overriding any of these:
         *  - `word`         the word that must be typed. **It is compared exactly**, as both of
         *                   today's modals compare it (`files/ConfirmDeleteModal.vue:24`,
         *                   `managed_storages/ConfirmDeleteModal.vue:10`): `delete` and `DELETE `
         *                   with a trailing space do NOT satisfy it. The test is not loosened.
         *  - `reason`       what the switched-off button says on its `title` until it is typed
         *  - `placeholder`  the input's placeholder (d7 draws the word itself)
         */
        ftdTypeShape(type){
            const given = (!type || type === true) ? {} : type;
            const word = given.word || 'DELETE';
            return {
                word: word,
                reason: given.reason || ('Type ' + word + ' first'),
                placeholder: given.placeholder || word,
                onInput: given.onInput || null
            };
        },

        /**
         * T33a. THE CONFIRM'S BODY: one shell, three shapes (`.ftd-confirm`).
         *  - `what`  the question, a string or the caller's own VNodes
         *  - `options.destructive`  TRUE, and only true, gives the red trash mark. Anything else --
         *                           including a `danger` variant on a dialogue that deactivates,
         *                           activates or notifies -- gives `--warn`, the reversible one.
         *  - `options.extra`   shape two: `.ftd-confirm__extra`, today's tick box
         *  - `options.type`    shape three: `.ftd-confirm__type`, the type-DELETE field, drawn
         *                      whole -- the `.ftd-field`, its label and its input -- and not a
         *                      wrapper round something a caller must supply. `presentConfirmBox`
         *                      switches the footer's Delete off around it.
         * Shape three is built here so the one shell owns it; T30 owns the two
         * `ConfirmDeleteModal.vue` files that adopt it, and T33a visits neither.
         */
        ftdConfirmBody(what, options = {}){
            const h = this.$createElement;
            // THE MARK is the destructive one (`.ftd-confirm__icon`, the bad plate) only where the
            // action destroys something; everything else takes `--warn` (fix round 1, defect 4).
            // THE GLYPH inside it is the trash for the plain delete, and `bx-error` for the
            // type-DELETE shape, which is exactly how d7 draws its own three
            // (`d7-messages-and-page-states.html:88`: the third confirm is
            // `.ftd-confirm__icon > i.bx.bx-error`). The mark itself does not change with it.
            const glyph = (options.destructive === true && !options.type) ? 'bx-trash' : 'bx-error';
            const children = [
                h('div', {
                    class: ['ftd-confirm__icon', options.destructive === true ? null : 'ftd-confirm__icon--warn']
                }, [
                    h('i', { class: ['bx', glyph] })
                ]),
                // the mock's `__what` is a `<p>`; a caller whose message is already a block of its
                // own (the 14 that pass `htmlToVNode`) gets a `<div>`, because a `<div>` inside a
                // `<p>` is reparented by the browser and the wrapper would be lost.
                h(options.what_tag || 'div', { class: 'ftd-confirm__what' }, [what])
            ];

            if (options.extra){
                children.push(h('div', { class: 'ftd-confirm__extra' }, [options.extra]));
            }
            if (options.type){
                // d7's own markup for shape three (`d7-messages-and-page-states.html:88`):
                // `.ftd-confirm__type` > `.ftd-field` > `.ftd-field__label` ("Please type
                // <b>DELETE</b> to confirm") over a `.ftd-input`. The sheet draws the word from
                // `.ftd-confirm__type b` (`film-tv-dashboard.css:861`) and the box from
                // `.ftd .ftd-field .ftd-input` (`:666`), so no look is written here.
                const shape = this.ftdTypeShape(options.type);
                children.push(h('div', { class: 'ftd-confirm__type' }, [
                    h('div', { class: 'ftd-field' }, [
                        h('span', { class: 'ftd-field__label' }, [
                            'Please type ',
                            h('b', shape.word),
                            ' to confirm'
                        ]),
                        h('input', {
                            class: 'ftd-input',
                            attrs: {
                                type: 'text',
                                placeholder: shape.placeholder,
                                autocomplete: 'off',
                                spellcheck: 'false'
                            },
                            on: {
                                input: (event) => {
                                    if (shape.onInput){
                                        shape.onInput(event.target.value);
                                    }
                                }
                            }
                        })
                    ])
                ]));
            }

            return [h('div', { class: 'ftd-confirm' }, children)];
        },

        /**
         * THE CONFIRM. The signature does not move and the promise does not move: this still
         * returns `$bvModal.msgBoxConfirm`'s own promise and still resolves true, false or null
         * exactly as it did, because 14 other callers plus `deleteItemByID` `.then` on it.
         * `YES` and `NO` stay in capitals (D7 Q1). The seventh parameter is new and is on the
         * END, so no existing argument moves: it is `ftdConfirmBody`'s own options, which is how
         * a caller reaches shapes two and three.
         */
        presentConfirmBox(title, message, variant = "success", size="sm", okTitle="YES", cancelTitle="NO", confirmOptions=null) {
            //
            // WHAT MAKES A CONFIRM DESTRUCTIVE IS THE ACTION, NOT THE VARIANT STRING (fix round 1,
            // defect 4). The first build read `variant === 'danger'`, and `danger` is what a caller
            // passes for "this matters", not for "this destroys": `SupplierActionsList.vue:133`
            // raises Confirm DEACTIVATION -- and Confirm ACTIVATION -- as `danger`, and both
            // `Confirm Action` dialogues (`customers/reusables/UsersList.vue:106`,
            // `users/List.vue:370`, which resend an activation or reset a token) are `danger` too.
            // A supplier activation was being given a red trash can.
            //
            // So a confirm destroys nothing unless it says it does, on the seventh parameter:
            // `deleteItemByID` opts in, and so does the type-DELETE shape, which exists only for a
            // deletion. Everything else takes `.ftd-confirm__icon--warn` and a plain `.ftd-btn`,
            // which is the brief's own rule (§6.4) for Confirm Deactivation, Change Status and
            // Cancel this record?. `variant` is still accepted in its own position and no call
            // site moves.
            //
            const options = Object.assign({}, confirmOptions || {});
            const is_destructive = options.destructive === true || !!options.type;
            const modal_id = 'ftd-confirm-' + (++ftdMessageSeq);

            //
            // SHAPE THREE, AND HOW THE FOOTER'S DELETE IS SWITCHED OFF (fix round 2, defect 1).
            // The task asks for `.ftd-confirm__type` with its own `.ftd-field` and input AND for
            // "the footer's Delete `.is-off` with its reason in the `title` until the word is
            // typed", with the footer reading Cancel and Delete. The body is `ftdConfirmBody`'s.
            // The footer is BootstrapVue's, and three ways in were tried before this one:
            //
            //  - `okDisabled` is not the way in. `msgBoxConfirm` overwrites it with `false` on
            //    every call (`bootstrap-vue/src/components/modal/helpers/bv-modal.js:242`), and a
            //    message box's props are set once as `propsData`. It would also draw Bootstrap's
            //    own `disabled` rather than the sheet's `.is-off` -- and `.is-off` is written
            //    `pointer-events: auto` on purpose (`film-tv-dashboard.css:633`) so that the
            //    reason on `title` can still be read, which a natively disabled button forbids.
            //  - PUTTING THE CLASS ON BOOTSTRAPVUE'S BUTTON BY HAND DOES NOT HOLD, and it is worth
            //    recording why. `BModal` keeps a `MutationObserver` over its own content with
            //    `attributeFilter: ['style', 'class']` (`modal.js:94-100`, `:394`), so a class
            //    written on to the button makes the modal re-measure and re-render, and the
            //    re-render writes the class list back without it. Re-asserting it -- on the next
            //    tick, on `shown`, on the box's `updated` hook, or on every animation frame --
            //    only feeds that loop: measured at `.is-off` present for two frames and absent
            //    from 200ms on, with the browser never going idle again.
            //  - So VUE RENDERS THE STATE, on a footer of our own. `BModal` renders the
            //    `modal-footer` slot in place of its two buttons when one is given
            //    (`modal.js:846-847`), it reads that slot through `normalizeSlot`, which prefers
            //    a `$scopedSlots` FUNCTION and therefore calls it on every render
            //    (`utils/normalize-slot.js:34-44`), and a message box has no parent vnode, so a
            //    slot put on the instance is not overwritten -- which is how `msgBoxConfirm`
            //    itself passes the title and the body (`bv-modal.js:137-142`). The two buttons are
            //    then d7's own markup exactly (`d7-messages-and-page-states.html:88`), and the
            //    promise is untouched: they call the modal's own `hide('ok')` / `hide('cancel')`,
            //    so the resolver still answers true, false or null as it does for the other 14
            //    callers (`bv-modal.js:246-249`).
            //
            // The message box instance itself is reachable because `msgBoxConfirm` builds it with
            // `createNewChildComponent(this._vm, ...)` (`bv-modal.js:114`), so it is a child of
            // the caller and carries our own `id`.
            //
            let msgBox = null;
            let type_shape = null;
            let type_satisfied = true;

            if (options.type){
                type_shape = this.ftdTypeShape(options.type);
                type_satisfied = false;
                // "Its footer is Cancel and Delete, not YES and NO, as today" (task Wanted 3,
                // fourth bullet). The helper's own defaults are the 81 delete sites' YES / NO, so
                // they are swapped only where they were left at those defaults; a caller that
                // names its own two words still keeps them.
                if (okTitle === 'YES'){ okTitle = 'Delete'; }
                if (cancelTitle === 'NO'){ cancelTitle = 'Cancel'; }
                options.type = Object.assign({}, type_shape, {
                    // THE WORD IS COMPARED EXACTLY, as both of today's modals compare it
                    // (`files/ConfirmDeleteModal.vue:24`, `managed_storages/...:10`): `delete` and
                    // `DELETE ` with a trailing space leave the button off. The test is not
                    // loosened. One re-render, and the footer below draws the new state.
                    onInput: (value) => {
                        // a caller that passed its own `onInput` still gets it: the switch is
                        // added to what it asked for, never in place of it
                        if (type_shape.onInput){
                            type_shape.onInput(value);
                        }
                        const satisfied = (value === type_shape.word);
                        if (satisfied === type_satisfied){
                            return;
                        }
                        type_satisfied = satisfied;
                        if (msgBox){
                            msgBox.$forceUpdate();
                        }
                    }
                });
            }

            const promise = this.$bvModal.msgBoxConfirm(
                this.ftdConfirmBody(message, Object.assign(options, { destructive: is_destructive })),
                {
                    id: modal_id,
                    title: title,
                    size: size,
                    buttonSize: 'sm',
                    okTitle: okTitle,
                    cancelTitle: cancelTitle,
                    hideHeaderClose: false,
                    centered: true,

                    // THE SHELL. `modalClass` is the `.ftd` ancestor for everything inside, which
                    // `msgBoxConfirm` renders at `<body>` and therefore outside every `.ftd` on
                    // the page; the other four put the house classes on BootstrapVue's own
                    // elements. `.ftd-modal--sm` is 440px and goes on the dialog, which is what
                    // centres it.
                    modalClass: 'ftd',
                    dialogClass: 'ftd-modal--sm',
                    contentClass: 'ftd-modal',
                    headerClass: 'ftd-modal__head',
                    titleClass: 'ftd-modal__title',
                    // THE TITLE IS A `<span>`, as d7 and r12 write it, and not BootstrapVue's default
                    // `<h5>` (second build, measured after the hand-off's Amendment 4). Between 768
                    // and 1199 wide the theme carries `body[data-sidebar="dark"] h1..h6 { color:
                    // #605d5d !important }`, which no selector of the sheet can beat: as an `h5` the
                    // title computed rgb(96,93,93) at 1024 where the mock's computes rgb(43,52,64).
                    // The mock's own tag is outside that rule's reach, so nothing is answered with
                    // an `!important` of ours (Amendment 4, rule d).
                    titleTag: 'span',
                    bodyClass: 'ftd-modal__body',
                    // THE FOOTER IS RIGHT-ALIGNED: `--right` (follow-up 2, the hand-off's Amendment 11
                    // item 3, Log #404). Log #367 has every footer of the shell right-aligned with
                    // the one submit rightmost, and YES is DOM-last here, so it is the rightmost
                    // control only if the row is pushed to the right. Without the modifier the
                    // run's own partial (`_ftd-messages.scss`, the `:not(.ftd-modal__foot--right)`
                    // rule) puts the pair at the LEFT, which is what that rule is for and is why it
                    // is not edited: the modifier is the sheet's own way of asking for the other end
                    // (`.ftd-modal__foot--right { justify-content: flex-end }`). It is decided HERE,
                    // on the one options object every confirm passes through, so none of the 81
                    // delete sites and 15 direct callers moves; the type-DELETE shape's own footer
                    // is a slot INSIDE this same element and takes it too.
                    footerClass: 'ftd-modal__foot ftd-modal__foot--right',

                    // BootstrapVue 2.23.1 has no `okClass`, `cancelClass` or `headerCloseClass`.
                    // It writes `btn-${okVariant}`, `btn-${cancelVariant}` and
                    // `text-${headerCloseVariant}` into those three elements' class lists, so a
                    // variant string is the one way to put a house class on them. The sheet then
                    // draws them: `.ftd .ftd-btn` (0,0,2) beats `.btn` (0,0,1).
                    //
                    // `none` is deliberately not a variant the theme knows, so the only classes
                    // that reach the element are `.btn`, `.btn-sm` and the house's. Naming a real
                    // one loses: the theme carries `.btn-secondary{color:#eff2f7!important}` and
                    // `.text-reset{color:inherit!important}`, and an `!important` beats the sheet
                    // however specific the sheet's selector is.
                    okVariant: 'none ftd-btn' + (is_destructive ? ' ftd-btn--danger' : ''),
                    cancelVariant: 'none ftd-pill',
                    headerCloseVariant: 'none ftd-modal__x',
                    headerCloseContent: '<i class="bx bx-x" aria-hidden="true"></i><span class="ftd-sr">Close</span>'
                }
            );

            this.ftdThemeOn(modal_id);

            if (type_shape){
                // the instance `msgBoxConfirm` just parented on us, found by our own id
                const children = this.$children || [];
                for (let i = children.length - 1; i >= 0 && !msgBox; i--){
                    if (children[i] && children[i].id === modal_id && typeof children[i].hide === 'function'){
                        msgBox = children[i];
                    }
                }

                if (msgBox){
                    // THE FOOTER, drawn as d7 draws it: `.ftd-pill` Cancel, then
                    // `.ftd-btn.ftd-btn--danger` Delete carrying `.is-off` and its reason on
                    // `title` until the word is typed. A scoped slot is a function, so this runs
                    // on every render of the modal and the state is always the current one.
                    msgBox.$scopedSlots = Object.assign({}, msgBox.$scopedSlots, {
                        'modal-footer': () => {
                            const h = msgBox.$createElement;
                            return [
                                h('button', {
                                    class: 'ftd-pill',
                                    attrs: { type: 'button' },
                                    on: { click: () => { msgBox.hide('cancel'); } }
                                }, cancelTitle),
                                h('button', {
                                    class: ['ftd-btn', 'ftd-btn--danger', type_satisfied ? null : 'is-off'],
                                    attrs: {
                                        type: 'button',
                                        // the reason is readable on hover because `.is-off` keeps
                                        // `pointer-events: auto`, which a `disabled` button does not
                                        title: type_satisfied ? null : type_shape.reason
                                    },
                                    // THE OFF STATE IS A REFUSAL, not only a look: until the word
                                    // is typed nothing hides the dialogue, so the promise the 15
                                    // callers await stays unresolved.
                                    on: { click: () => { if (type_satisfied){ msgBox.hide('ok'); } } }
                                }, okTitle)
                            ];
                        }
                    });

                    // the input is focused once the dialogue is up, as today's own modal focuses
                    // it (`files/ConfirmDeleteModal.vue`'s `@shown="focusDeleteInput"`).
                    msgBox.$on('shown', () => {
                        const modal_element = document.getElementById(modal_id);
                        const input = modal_element ? modal_element.querySelector('.ftd-confirm__type .ftd-input') : null;
                        if (input){
                            input.focus();
                        }
                    });
                }else{
                    // The instance was not found, so the footer is BootstrapVue's own and cannot
                    // be drawn off. Refuse the press in the DOM instead: a capture listener above
                    // the button runs before BootstrapVue's own click handler on it.
                    const guard = (event) => {
                        if (type_satisfied){
                            return;
                        }
                        const modal_element = document.getElementById(modal_id);
                        const button = modal_element ? modal_element.querySelector('.modal-footer .ftd-btn') : null;
                        if (button && (event.target === button || button.contains(event.target))){
                            event.preventDefault();
                            event.stopPropagation();
                        }
                    };
                    document.addEventListener('click', guard, true);
                    const drop = () => { document.removeEventListener('click', guard, true); };
                    promise.then(drop).catch(drop);
                }
            }

            return promise;
        },
        htmlToVNode(html, required_class='') {
            return [
                this.$createElement('div', { domProps: { innerHTML: html}, class: required_class })
            ]
        },
        hasPermission(key, permissions_list=null){
            if (permissions_list){
                if (permissions_list instanceof Object){
                    permissions_list = Object.values(permissions_list);
                }

                return permissions_list ? permissions_list.includes(key) : false;
            }

            if (this.currentUserPermissions){
                return this.currentUserPermissions.includes(key);
            }

            return false;
        },
        setDeleteCheckBox(value){
            this.delete_checkbox = value;
        },
        deleteItemByID(item_id, item_name, item_type, event, checkbox_message='', refusal=null) {

            return new Promise( (resolve, reject) => {
                if (event){
                    const h = this.$createElement;

                    // T30 (Log #370, r9 screen 5). A REFUSED DELETE SAYS WHY INSIDE THE DIALOGUE THAT ASKED.
                    // The server only answers after YES, and YES is what closes BootstrapVue's message
                    // box, so the dialogue that asked is asked AGAIN with the reason above its question:
                    // this method calls itself with `refusal` = { msg, response }, the sixth parameter, on
                    // the END so no call site's argument moves (none of them passes it). The words are
                    // the server's own `msg`, untouched, the ones that were a toast. The strip is the
                    // sheet's `.ftd-strip`, exactly as r9 writes it, and it is put ABOVE `.ftd-confirm`
                    // as the first child of `.ftd-modal__body` further down, once the box exists:
                    // `ftdConfirmBody` wraps whatever it is given inside `.ftd-confirm__what`, and that
                    // method and `presentConfirmBox` are T33a's and are not touched.
                    //
                    // WALK HAND-OFF 2026-10-02, ENTRIES 1 AND 2 (unit WD). A refusal whose reply carries an
                    // `offer` (`{ kind: 'links' | 'branch', question, type_delete?, branch_count? }`) is not
                    // asked again under a strip: its `question` is asked in place of "Are you sure", and its
                    // YES posts the delete again with the consent beside `delete_checkbox`. Only such a
                    // reply reaches this; every other call of the 81 is as it was.
                    const offer = (refusal && refusal.offer && typeof refusal.offer.question === 'string' && refusal.offer.question !== '')
                        ? refusal.offer : null;

                    const refusalVContent = (!offer && refusal && refusal.msg) ? h('div', { class: 'ftd-strip' }, [
                        h('i', { class: ['bx', 'bx-error-circle'] }),
                        h('span', refusal.msg)
                    ]) : null;

                    // T33a. Today's words exactly, with the record's name in bold, drawn as the
                    // sheet's `.ftd-confirm__what` (d7 option A, `d7-messages-and-page-states.html:88`).
                    const questionVContent = offer ? [offer.question] : [
                        'Are you sure you want to delete ',
                        h('b', item_name),
                        '?',
                    ];

                    // shape two: today's tick box and today's words, under a hairline
                    let checkboxVNode = null;
                    if(checkbox_message !== ''){
                        checkboxVNode = h("b-form-checkbox", {
                                class: {
                                    'custom-checkbox-af-accent': true
                                },
                                attrs: {
                                    name: `delete_checkbox`,
                                    value: true,
                                },
                                domProps: {
                                    value: this.delete_checkbox
                                },
                                on: {
                                    change: this.setDeleteCheckBox
                                }
                            },
                            [
                                h('span', { class: ['text-danger'] } , checkbox_message)
                            ]);
                    }

                    // THE ONE DIALOGUE THAT REALLY DESTROYS SOMETHING, and the one that asks for
                    // the red trash mark and the red YES (fix round 1, defect 4). It is the 81
                    // delete call sites' own path; nothing else opts in.
                    const confirmOptions = {
                        destructive: true,
                        what_tag: 'p',
                        extra: checkboxVNode
                    };
                    // Entry 2: the branch offer, and only it, asks for the word (the type-DELETE shape).
                    if (offer && offer.kind === 'branch' && offer.type_delete){
                        confirmOptions.type = true;
                    }
                    const confirmPromise = this.presentConfirmBox('Confirm Deletion', questionVContent, 'danger', 'md', 'YES', 'NO', confirmOptions);

                    // T30. The message box `presentConfirmBox` has just parented on us, found by the id
                    // it was given (the same search T33a's type-DELETE shape makes). It is mounted within
                    // this tick, and a message box has no parent vnode, so its `$slots.default` -- where
                    // `msgBoxConfirm` itself put the body -- is ours to lead with the strip. Where no box
                    // can be found (a caller that replaces `presentConfirmBox`), `confirmBox` stays null
                    // and a refusal is toasted and resolved exactly as it was before T30.
                    let confirmBox = null;
                    const confirmBoxId = 'ftd-confirm-' + ftdMessageSeq;
                    const siblings = this.$children || [];
                    for (let i = siblings.length - 1; i >= 0 && !confirmBox; i--){
                        if (siblings[i] && siblings[i].id === confirmBoxId && typeof siblings[i].hide === 'function'){
                            confirmBox = siblings[i];
                        }
                    }
                    if (confirmBox && refusalVContent){
                        confirmBox.$slots.default = [refusalVContent].concat(confirmBox.$slots.default || []);
                        confirmBox.$forceUpdate();
                    }

                    confirmPromise
                        .then(value => {
                            if (value){
                                const deleteData = {
                                    delete_checkbox: this.delete_checkbox
                                };
                                // the consent the offer asked for, posted beside the tick box's
                                if (offer){
                                    if (offer.kind === 'branch'){
                                        deleteData.delete_branch = offer.branch_count;
                                    }else{
                                        deleteData.remove_links = 1;
                                    }
                                }
                                axios({
                                    method:'delete',
                                    url:'/api/assetflow/'+item_type+'/'+item_id,
                                    baseURL: '/',
                                    data: deleteData
                                }).then(response => {
                                    let status = response.data.data.status;
                                    let msg = response.data.data.msg;

                                    response.msg = msg;

                                    // Entries 1 and 2: a refusal that carries an offer asks the offer (no
                                    // strip, no second "Are you sure"), and the CALLER hears the FINAL
                                    // answer, as the tick box's path below does: the success reply after
                                    // YES, the offer reply after NO.
                                    const replyOffer = response.data.data.offer;
                                    if (!status && replyOffer && typeof replyOffer.question === 'string' && replyOffer.question !== ''){
                                        this.deleteItemByID(item_id, item_name, item_type, event, checkbox_message, {
                                            msg: msg,
                                            response: response,
                                            offer: replyOffer
                                        }).then(resolve, reject);
                                        return;
                                    }

                                    // T30. Refused: no toast. The same dialogue is asked again with the
                                    // server's sentence above the question.
                                    //  - THE CALLER IS TOLD AT ONCE, as it always was: this promise
                                    //    resolves with the refusal now, so a page that stays put, prints
                                    //    the reason or keeps its row on `status: false` does so exactly
                                    //    when it did. What the second dialogue ends with is then nobody's
                                    //    to hear, and its rejection (a page torn down under it) is caught.
                                    //  - THE ONE EXCEPTION IS THE TICK BOX ("Delete {name} even if not
                                    //    empty"): there the user can change the server's answer from
                                    //    inside the second dialogue, so the caller hears the FINAL
                                    //    answer instead -- the success, and its list is refreshed, or
                                    //    this refusal once NO is pressed.
                                    if (!status && confirmBox){
                                        const askedAgain = this.deleteItemByID(item_id, item_name, item_type, event, checkbox_message, {
                                            msg: msg,
                                            response: response
                                        });
                                        if (checkbox_message !== ''){
                                            askedAgain.then(resolve, reject);
                                        }else{
                                            askedAgain.then(function () {}, function () {});
                                            resolve(response);
                                        }
                                        return;
                                    }

                                    this.presentToast((status ? "Saved" : "Not saved"),
                                        msg,
                                        (status ? 'success' : 'danger'),
                                        4000,
                                        true,
                                        true);
                                    resolve(response);
                                })
                            }else{
                                if (item_type === 'assets'){
                                    this.selected_asset_id = null;
                                }else if (this.hasOwnProperty('selected_object_id') && this.selected_object_id){
                                    this.selected_object_id = null;
                                }

                                // T30. A NO (or a close) after a refusal ends the delete. Where the
                                // caller is still waiting (the tick box, above) it is handed the
                                // server's refusal, which it was handed before T30 too.
                                if (refusal && refusal.response){
                                    resolve(refusal.response);
                                }
                            }
                        }).catch((error) => {
                            reject(error);
                        });
                }else{
                    reject(new Error('Event isn\'t defined!'));
                }
            });
        },
        performAssetBulkAction(action, item_type, selected_items, value, event){
            return new Promise( (resolve, reject) => {
                if (event){
                    axios({
                        method:'post',
                        url:'/api/assetflow/'+item_type+'/bulk_action',
                        baseURL: '/',
                        data: {
                            action: action,
                            selected_items: selected_items,
                            value: value
                        }
                    }).then(response => {
                        let status = response.data.data.status;
                        let msg = response.data.data.msg;
                        this.presentToast((status ? "Saved" : "Not saved"),
                            msg,
                            (status ? 'success' : 'danger'),
                            4000,
                            true,
                            true);
                        resolve(response);
                    })
                }else{
                    reject(new Error('Event isn\'t defined!'));
                }
            })
        }

    }
}
